import { useState, useEffect } from 'react'
import { Bell, BellOff, Hash, Loader2, RefreshCw, Send, AlertTriangle, ChevronDown, ChevronRight, MessageSquare, Info, Sparkles, Search, Building2, CheckCircle2, Circle, Shield, Hammer, FileCheck, BarChart3, DollarSign, Paperclip, Trash2, ExternalLink } from 'lucide-react'
import { pocApi, getAuthHeaders } from '@/api/client'
import { showToast } from '@/store/uiStore'
import { Card } from '@/components/ui/Card'
import type { PocRecord, CheckType } from '../types'
import { COUNTRIES } from '../data/countries'
import { CHECK_TYPE_OPTIONS, CONSENT_OPTIONS, ADDRESS_MATCH_FIELDS, MATCH_ELEMENTS, deriveMatchLabel } from '../data/discoveryChecklist'
import { VENDORS } from '../data/vendorMetadata'
import { PocAiAssist } from './PocAiAssist'
import { DInput, DTextarea, MultiPick, PocClientGuide, ic, lc } from '../index'

// ---- Helpers ----

interface SlackMessage {
  id: number
  poc_id: number
  message_type: string
  slack_ts: string | null
  channel_id: string
  message_text: string
  people_tagged: string | null
  created_at: string
}

function countryName(code: string): string {
  return COUNTRIES.find(c => c.code === code)?.name || code
}

function countryNames(codes: string[]): string {
  return codes.map(c => countryName(c)).join(', ')
}

// ---- Message Templates ----

interface PocContext {
  industry: string
  background: string
  problems: string
}

const PRE_POC_TEMPLATES = [
  {
    id: 'kickoff_prep',
    label: '\ud83c\udfaf Pre-POC Kickoff Prep',
    description: 'Meeting agenda, research checklist, and what to cover with the customer',
    getMessage: (poc: PocRecord, _ctx?: PocContext) => {
      const name = poc.company_name || poc.deal_name
      const countries = poc.countries_in_scope || []
      const checkTypes = poc.check_types || []
      const lines = [
        `\ud83c\udfaf *Pre-POC Kickoff Prep: ${name}*`,
        '',
        `Hey team! \ud83d\udc4b We're gearing up for the *${name}* POC kickoff. Before we meet with the customer, let's make sure we've done our homework.`,
        '',
        `\ud83d\udccc *POC Scope:*`,
      ]
      if (countries.length > 0) lines.push(`  \ud83c\udf0d Countries: ${countryNames(countries)}`)
      if (checkTypes.length > 0) lines.push(`  \ud83d\udd0d Check Types: ${checkTypes.join(', ')}`)
      lines.push(
        '',
        `\u2705 *Pre-Meeting Checklist:*`,
        `  \u2610 Have we run qualification for all countries in scope?`,
        `  \u2610 Do we know what data the customer can provide (PII fields)?`,
        `  \u2610 Have we identified the right vendors and data sources per country?`,
        `  \u2610 Do we understand their current pass rate and target?`,
        `  \u2610 Have we checked consent requirements per country?`,
        `  \u2610 Is the vendor coordination plan ready?`,
        '',
        `\ud83d\uddd3\ufe0f *Meeting Agenda:*`,
        `  1\ufe0f\u20e3 Confirm scope: countries, check types, volumes`,
        `  2\ufe0f\u20e3 Discuss data availability \u2014 what PII can they provide?`,
        `  3\ufe0f\u20e3 Set expectations on pass rates from our qualification`,
        `  4\ufe0f\u20e3 Agree on data transfer method & timeline`,
        `  5\ufe0f\u20e3 Define success criteria together`,
        `  6\ufe0f\u20e3 Next steps and owners`,
        '',
        `\ud83d\udcac Let's discuss any gaps in this channel before the call! \ud83d\ude4f`,
      )
      return lines.join('\n')
    },
  },
  {
    id: 'data_request',
    label: '\ud83d\udce6 Data Readiness Check',
    description: 'Check data requirements and follow up on delivery status',
    getMessage: (poc: PocRecord) => {
      const name = poc.company_name || poc.deal_name
      const countries = poc.countries_in_scope || []
      const lines = [
        `\ud83d\udce6 *Data Readiness Check: ${name}*`,
        '',
        `Quick check-in on the data side of this POC \ud83d\udc40`,
        '',
        `\ud83d\udcdd *What we need from the customer:*`,
      ]
      if (countries.length > 0) {
        lines.push(`  \ud83c\udf0d Test data for: *${countryNames(countries)}*`)
      } else {
        lines.push(`  \ud83c\udf0d Test data (countries TBD)`)
      }
      lines.push(
        `  \ud83d\udccb Data should include all required PII fields per country`,
        `  \ud83c\udff7\ufe0f Ideally with ground-truth labels (verified/not verified)`,
        `  \ud83d\udcca Minimum recommended: 500\u20131,000 records per country`,
        '',
        `\u2753 *Questions to resolve:*`,
        `  \u2022 Has the customer confirmed the data transfer method?`,
        `  \u2022 Do we have the right file format specs shared?`,
        `  \u2022 Timeline for data delivery?`,
        `  \u2022 Any PII or consent constraints?`,
        '',
        `\ud83d\udc47 Please reply with the current status below!`,
      )
      return lines.join('\n')
    },
  },
  {
    id: 'results_ready',
    label: '\ud83d\udcca Results Ready',
    description: 'Notify the team that results are in and ready for review',
    getMessage: (poc: PocRecord) => {
      const name = poc.company_name || poc.deal_name
      return [
        `\ud83c\udf89 *POC Results Ready: ${name}*`,
        '',
        `Great news! The results for the *${name}* POC are now available in DealHub \ud83d\ude80`,
        '',
        `\ud83d\udcdd *Next steps:*`,
        `  1\ufe0f\u20e3 Review pass rates by vendor and country in the Results tab`,
        `  2\ufe0f\u20e3 Compare against customer's current rates and our targets`,
        `  3\ufe0f\u20e3 Prepare the results summary for customer presentation`,
        `  4\ufe0f\u20e3 Flag any countries or vendors needing follow-up`,
        '',
        `\ud83d\udcac Let's align on the story before presenting. Tag relevant team members below \ud83d\udc47`,
      ].join('\n')
    },
  },
]

// ---- Component ----

export function PocOverview({ poc, save, anonymised, anonLabel }: { poc: PocRecord; save: (data: Partial<PocRecord>) => void; anonymised?: boolean; anonLabel?: string }) {
  const disc = (poc.discovery_json as any) || {}
  const [localDisc, setLocalDisc] = useState<Record<string, any>>(() => disc)
  const [guideOpen, setGuideOpen] = useState(false)

  // Reset local state when switching POCs
  useEffect(() => { setLocalDisc((poc.discovery_json as any) || {}) }, [poc.id])
  const saveDisc = (d: Record<string, any>) => { setLocalDisc(d); save({ discovery_json: d as any }) }

  // Slack state
  const [slackMessages, setSlackMessages] = useState<SlackMessage[]>([])
  const [remindersLoading, setRemindersLoading] = useState(false)
  const [resendingId, setResendingId] = useState<number | null>(null)
  const [customMessage, setCustomMessage] = useState('')
  const [sendingCustom, setSendingCustom] = useState(false)
  const [sendingTemplate, setSendingTemplate] = useState<string | null>(null)
  const [messagesOpen, setMessagesOpen] = useState(false)
  const [connectProgress, setConnectProgress] = useState('')
  const [loadingPassRates, setLoadingPassRates] = useState(false)
  const [refreshingCanvas, setRefreshingCanvas] = useState(false)

  // Customer context
  const [context, setContext] = useState<PocContext>({
    industry: disc.comms_industry || '',
    background: disc.comms_background || disc.what_problem || '',
    problems: disc.comms_problems || disc.current_pain_points || '',
  })
  const [sendingIntro, setSendingIntro] = useState(false)

  useEffect(() => {
    const d = (poc.discovery_json as any) || {}
    setContext(prev => ({
      industry: prev.industry || d.comms_industry || '',
      background: prev.background || d.comms_background || d.what_problem || '',
      problems: prev.problems || d.comms_problems || d.current_pain_points || '',
    }))
  }, [poc.discovery_json])

  // Deal search
  const [dealQuery, setDealQuery] = useState('')
  const [dealResults, setDealResults] = useState<any[]>([])
  const [dealSearching, setDealSearching] = useState(false)
  const [dealDropdownOpen, setDealDropdownOpen] = useState(false)

  useEffect(() => {
    if (dealQuery.length < 2) { setDealResults([]); return }
    const t = setTimeout(async () => {
      setDealSearching(true)
      try {
        const r = await fetch(`/api/hubspot/search?q=${encodeURIComponent(dealQuery)}`, { headers: getAuthHeaders() })
        if (r.ok) { const d = await r.json(); setDealResults(d?.data?.results || d?.results || []); setDealDropdownOpen(true) }
      } catch { /* ignore */ }
      setDealSearching(false)
    }, 400)
    return () => clearTimeout(t)
  }, [dealQuery])

  const handleDealSelect = async (d: any) => {
    const name = d.properties?.dealname || ''
    const owner = d._ownerName || ''
    const company = d._companyName || ''
    setDealQuery(name)
    setDealDropdownOpen(false)
    const updates: Partial<PocRecord> = { deal_name: name, presales_owner: owner || poc.presales_owner || '', company_name: company || poc.company_name || '' }
    const discUpdates: Record<string, any> = { ...localDisc }
    if (d.id) {
      discUpdates.hubspot_deal_id = d.id
      try {
        const res = await pocApi.lookupDeal(d.id, name, company)
        const resData = (res?.data as any)?.data ?? res?.data
        if (resData?.presales_deal_id) updates.presales_deal_id = resData.presales_deal_id
        if (resData?.scope_deal_id) updates.scope_deal_id = resData.scope_deal_id
      } catch { /* non-blocking */ }
    }
    updates.discovery_json = discUpdates as any
    setLocalDisc(discUpdates)
    save(updates)
  }

  // Slack messages
  useEffect(() => {
    if (poc.slack_channel_id) {
      pocApi.getSlackMessages(poc.id).then(res => {
        const data = (res?.data as any)?.data ?? res?.data
        if (Array.isArray(data)) setSlackMessages(data)
      })
    }
  }, [poc.id, poc.slack_channel_id])

  const refreshMessages = async () => {
    const msgsRes = await pocApi.getSlackMessages(poc.id)
    const data = (msgsRes?.data as any)?.data ?? msgsRes?.data
    if (Array.isArray(data)) setSlackMessages(data)
  }

  const saveContext = (updates: Partial<PocContext>) => {
    const next = { ...context, ...updates }
    setContext(next)
    save({
      discovery_json: {
        ...localDisc,
        comms_industry: next.industry,
        comms_background: next.background,
        comms_problems: next.problems,
      } as any,
    })
  }

  const countries = poc.countries_in_scope || []
  const hasConsiderations = !!(disc.considerations_text)
  const countryOpts = COUNTRIES.map(c => ({ id: c.code, label: `${c.name} (${c.code})` }))

  // ---- Slack handlers ----

  const handleStartStop = async () => {
    if (remindersLoading) return
    setRemindersLoading(true)
    try {
      if (poc.reminders_active) {
        const res = await pocApi.stopReminders(poc.id)
        if (res.success) {
          save({ reminders_active: false })
          showToast('Communications stopped', 'success')
        } else {
          showToast((res as any).error || 'Failed to stop', 'error')
        }
      } else {
        if (!poc.slack_channel_id) {
          showToast('Set a channel ID first', 'error')
          setRemindersLoading(false)
          return
        }
        setConnectProgress('Connecting channel...')
        const res = await pocApi.startReminders(poc.id)
        if (!res.success) {
          showToast((res as any).error || 'Failed to start', 'error')
          setRemindersLoading(false)
          setConnectProgress('')
          return
        }
        // Create canvas BEFORE saving reminders_active — save triggers a re-render
        // that can kill this async function mid-execution
        setConnectProgress('Creating canvas...')
        let canvasSuccess = false
        try {
          const canvasRes = await pocApi.createCanvas(poc.id)
          if (canvasRes.success) {
            const canvasId = (canvasRes.data as any)?.status_canvas_id
            save({
              reminders_active: true,
              reminders_started_at: new Date().toISOString(),
              ...(canvasId ? { discovery_json: { ...disc, canvas_id: canvasId, canvas_channel_id: poc.slack_channel_id } as any } : {}),
            })
            canvasSuccess = true
          } else {
            // Save connection state even if canvas failed
            save({ reminders_active: true, reminders_started_at: new Date().toISOString() })
            showToast(`Connected but canvas failed: ${(canvasRes as any).error || 'Unknown error'}`, 'error')
          }
        } catch (canvasErr: any) {
          save({ reminders_active: true, reminders_started_at: new Date().toISOString() })
          showToast(`Connected but canvas failed: ${canvasErr?.message || 'Unknown error'}`, 'error')
        }
        if (canvasSuccess) showToast('Channel connected and canvas created.', 'success')
      }
    } catch (err: any) {
      console.error('[poc-connect] Error:', err)
      showToast(`Connection error: ${err?.message || 'Unknown error'}`, 'error')
    }
    setRemindersLoading(false)
    setConnectProgress('')
  }

  const handleSendTemplate = async (template: typeof PRE_POC_TEMPLATES[0]) => {
    if (!poc.slack_channel_id) { showToast('Connect a channel first', 'error'); return }
    setSendingTemplate(template.id)
    try {
      const message = template.getMessage(poc, context)
      const res = await pocApi.postStageUpdate(poc.id, message)
      if (res.success) { showToast('Message posted', 'success'); await refreshMessages() }
      else showToast((res as any).error || 'Failed', 'error')
    } catch { showToast('Failed to deliver message', 'error') }
    setSendingTemplate(null)
  }

  const handleSendCustom = async () => {
    if (!customMessage.trim() || !poc.slack_channel_id) return
    setSendingCustom(true)
    try {
      const res = await pocApi.postStageUpdate(poc.id, customMessage.trim())
      if (res.success) { showToast('Message posted', 'success'); setCustomMessage(''); await refreshMessages() }
      else showToast((res as any).error || 'Failed', 'error')
    } catch { showToast('Failed to deliver message', 'error') }
    setSendingCustom(false)
  }

  const handleSendIntro = async () => {
    if (!poc.slack_channel_id || !poc.reminders_active) return
    setSendingIntro(true)
    try {
      const name = poc.company_name || poc.deal_name
      const checkTypes = poc.check_types || []
      const lines = [`\ud83c\udfe2 *About ${name}*`, '']
      if (context.industry) lines.push(`\ud83c\udfed *Industry:* ${context.industry}`)
      if (context.background) lines.push(`\ud83d\udcd6 *Background:* ${context.background}`)
      if (context.problems) lines.push(`\ud83d\udea9 *What they're trying to solve:* ${context.problems}`)
      lines.push('')
      if (countries.length > 0) lines.push(`\ud83c\udf0d *Countries:* ${countryNames(countries)}`)
      if (checkTypes.length > 0) lines.push(`\ud83d\udd0d *Check Types:* ${checkTypes.join(', ')}`)
      if (poc.current_pass_rate) lines.push(`\ud83d\udcca *Current Pass Rate:* ${poc.current_pass_rate}%`)
      if (poc.success_criteria) lines.push(`\ud83c\udfaf *Success Criteria:* ${poc.success_criteria}`)
      const res = await pocApi.postStageUpdate(poc.id, lines.join('\n'))
      if (res.success) { showToast('Intro posted', 'success'); await refreshMessages() }
      else showToast((res as any).error || 'Failed', 'error')
    } catch { showToast('Failed to deliver message', 'error') }
    setSendingIntro(false)
  }

  const suggestedChannelName = `poc-${(poc.company_name || 'customer').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')}`
  const hasContext = context.industry || context.background || context.problems
  const isLinked = !!poc.presales_deal_id
  const hasHubspot = isLinked || !!(localDisc.hubspot_deal_id)

  // Phase progress
  const p1Done = [
    (poc.check_types || []).length > 0,
    countries.length > 0,
    hasContext,
    hasConsiderations,
  ].filter(Boolean).length
  const p1Total = 4

  const countryConfig = (localDisc.country_config || {}) as Record<string, any>
  const hasRatesForAll = countries.length > 0 && countries.every(c => countryConfig[c]?.current_rate)
  const hasTargetsForAll = countries.length > 0 && countries.every(c => countryConfig[c]?.target_rate)
  const p2Done = [
    hasRatesForAll,
    hasTargetsForAll,
    !!poc.success_criteria,
    !!(localDisc.business_case_impact),
    !!(localDisc.client_committed),
  ].filter(Boolean).length
  const p2Total = 5

  const hasMatchConfig = countries.length > 0 && countries.some(c => {
    const cc = countryConfig[c] || {}
    return (cc.match_source_1?.length > 0 || cc.match_source_2?.length > 0)
  })
  const p3Done = [
    hasMatchConfig,
    countries.some(c => (countryConfig[c]?.vendors || []).length > 0),
    countries.length > 0 && countries.every(c => (countryConfig[c] || {}).client_confirmed),
    !!poc.slack_channel_id,
  ].filter(Boolean).length
  const p3Total = 4

  // Phase helper
  const phaseHdr = (step: number, icon: React.ReactNode, title: string, subtitle: string, done: number, total: number) => (
    <div className="flex items-center gap-3 pt-6 pb-2 first:pt-0">
      <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${done === total ? 'bg-green-500/20 text-green-400 border border-green-500/40' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)]'}`}>
        {done === total ? <CheckCircle2 size={14} /> : step}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-bold text-[var(--text-primary)]">{title}</span>
          <span className="text-[10px] text-[var(--text-muted)]">{done}/{total}</span>
        </div>
        <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{subtitle}</p>
      </div>
      <div className="w-16 h-1.5 rounded-full bg-[var(--bg-tertiary)] overflow-hidden">
        <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${total > 0 ? (done / total) * 100 : 0}%` }} />
      </div>
    </div>
  )

  return (
    <div className="space-y-3 py-4">
      <PocClientGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      {/* ── Deal & Contact ── */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Deal & Contact</h3>
          <PocAiAssist
            poc={poc}
            scopeDealId={poc.scope_deal_id ?? null}
            onUpdate={(data) => {
              const updatedD = { ...localDisc, ...(data.discovery_json || {}) } as Record<string, unknown>
              setLocalDisc(updatedD)
              save({ ...data, discovery_json: updatedD as any })
              const newCtx = { ...context }
              if (updatedD.comms_industry) newCtx.industry = updatedD.comms_industry as string
              if (updatedD.comms_background) newCtx.background = updatedD.comms_background as string
              else if (!context.background && updatedD.what_problem) newCtx.background = updatedD.what_problem as string
              if (updatedD.comms_problems) newCtx.problems = updatedD.comms_problems as string
              else if (!context.problems && updatedD.current_pain_points) newCtx.problems = updatedD.current_pain_points as string
              if (newCtx.industry !== context.industry || newCtx.background !== context.background || newCtx.problems !== context.problems) {
                setContext(newCtx)
                saveContext(newCtx)
              }
            }}
          />
        </div>

        {anonymised ? (
          <div className="border border-[var(--border-color)] rounded-lg bg-[var(--bg-tertiary)] px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="shrink-0 w-7 h-7 rounded-md bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
                <Building2 size={14} className="text-violet-400" />
              </div>
              <div>
                <div className="text-sm font-medium text-violet-400">Company {anonLabel}</div>
                <div className="text-xs text-[var(--text-muted)]">Names hidden</div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="border border-[var(--border-color)] rounded-lg bg-[var(--bg-tertiary)] px-3 py-2">
              {poc.deal_name ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="shrink-0 w-7 h-7 rounded-md bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                      <Building2 size={14} className="text-cyan-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[var(--text-primary)] truncate">{poc.deal_name}</div>
                      <div className="text-xs text-[var(--text-muted)] truncate">
                        {hasHubspot ? `HubSpot deal${poc.company_name ? ` \u2022 ${poc.company_name}` : ''}` : `${poc.company_name || 'No company'} \u2022 Not linked to HubSpot`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setDealQuery(''); setDealResults([]); save({ deal_name: '', company_name: null, presales_deal_id: null }) }}
                    className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                    {dealSearching ? <Loader2 size={14} className="text-[var(--text-muted)] animate-spin" /> : <Search size={14} className="text-[var(--text-muted)]" />}
                  </div>
                  <input
                    type="text"
                    value={dealQuery}
                    onChange={e => { setDealQuery(e.target.value); setDealDropdownOpen(true) }}
                    onFocus={() => dealQuery.length >= 2 && setDealDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setDealDropdownOpen(false), 250)}
                    placeholder="Search HubSpot deals..."
                    className="w-full h-9 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md pl-8 pr-3 text-sm text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                  />
                  {dealDropdownOpen && dealResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] shadow-xl">
                      {dealResults.map((d: any) => (
                        <button
                          key={d.id}
                          className="w-full px-3 py-2 text-left hover:bg-[var(--bg-tertiary)] transition-colors border-b border-[var(--border-color)] last:border-b-0"
                          onMouseDown={() => handleDealSelect(d)}
                        >
                          <div className="text-sm font-medium text-[var(--text-primary)] truncate">{d.properties?.dealname}</div>
                          <div className="text-xs text-[var(--text-muted)]">
                            {d._companyName && <span>{d._companyName} &middot; </span>}
                            {d._ownerName || d.properties?.hubspot_owner_id} &middot; {d.properties?.dealstage}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className={lc}>Deal Name</label><DInput value={poc.deal_name} onSave={v => save({ deal_name: v })} className={`mt-1 ${ic}`} /></div>
              <div><label className={lc}>Company</label><DInput value={poc.company_name || ''} onSave={v => save({ company_name: v })} className={`mt-1 ${ic}`} /></div>
              <div><label className={lc}>Contact</label><DInput value={(localDisc.contact_name as string) || ''} onSave={v => saveDisc({ ...localDisc, contact_name: v })} className={`mt-1 ${ic}`} placeholder="e.g. Mazen, Ops Manager" /></div>
              <div><label className={lc}>Account Owner</label><DInput value={poc.presales_owner || ''} onSave={v => save({ presales_owner: v })} className={`mt-1 ${ic}`} placeholder="e.g. Lindsay Hatton" /></div>
            </div>
          </>
        )}
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════
          PHASE 1: ESTABLISH AUTHORITY
          Know their countries, understand the nuances, show we can deliver
         ═══════════════════════════════════════════════════════════════════ */}
      {phaseHdr(1, <Shield size={14} className="text-violet-400" />, 'Establish Authority',
        'Understand their countries, show we know the nuances, demonstrate our pass rates', p1Done, p1Total)}

      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Scope & Countries</h3>
        <div>
          <label className={lc}>Check Types</label>
          <div className="flex gap-2 mt-1">
            {CHECK_TYPE_OPTIONS.map(o => {
              const on = (poc.check_types || []).includes(o.id as CheckType)
              return <button key={o.id} type="button" onClick={() => {
                const cur = poc.check_types || []
                save({ check_types: on ? cur.filter(c => c !== o.id) : [...cur, o.id as CheckType] })
              }} className={`px-3 py-1.5 rounded text-xs font-medium border cursor-pointer ${on ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' : 'bg-[var(--bg-tertiary)] text-[var(--text-muted)] border-[var(--border-color)]'}`}>
                {on && '\u2713 '}{o.id}
              </button>
            })}
          </div>
        </div>
        <div>
          <label className={lc}>Countries</label>
          <div className="mt-1"><MultiPick options={countryOpts} selected={poc.countries_in_scope || []} onChange={c => save({ countries_in_scope: c })} placeholder="Search countries..." /></div>
        </div>
      </Card>

      {/* Customer Context — moved up to Phase 1 */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-violet-400" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Customer Context</h3>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          Understand who they are and what they need. Use AI Assist above to auto-fill from deal transcripts.
        </p>
        <div className="space-y-3">
          <div>
            <label className={lc}>Industry</label>
            <input type="text" value={context.industry}
              onChange={e => setContext(prev => ({ ...prev, industry: e.target.value }))}
              onBlur={() => saveContext({ industry: context.industry })}
              placeholder="e.g. Banking, Fintech, Gaming, Crypto exchange..."
              className={`mt-1 ${ic}`} />
          </div>
          <div>
            <label className={lc}>Background</label>
            <textarea value={context.background}
              onChange={e => setContext(prev => ({ ...prev, background: e.target.value }))}
              onBlur={() => saveContext({ background: context.background })}
              placeholder="What does this company do? e.g. Neo-bank targeting Gen Z, licensed in 3 EU markets..."
              rows={4} className={`mt-1 ${ic} resize-none`} />
          </div>
          <div>
            <label className={lc}>Problems they're trying to solve</label>
            <textarea value={context.problems}
              onChange={e => setContext(prev => ({ ...prev, problems: e.target.value }))}
              onBlur={() => saveContext({ problems: context.problems })}
              placeholder="e.g. Pass rates too low in LATAM markets, current vendor doesn't cover KYB..."
              rows={4} className={`mt-1 ${ic} resize-none`} />
          </div>
        </div>
      </Card>

      {/* Considerations status */}
      <Card className={`p-4 ${hasConsiderations ? 'border-green-500/20' : 'border-amber-500/20'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasConsiderations ? <CheckCircle2 size={14} className="text-green-400" /> : <AlertTriangle size={14} className="text-amber-400" />}
            <span className="text-sm font-semibold text-[var(--text-primary)]">Country Considerations</span>
            {hasConsiderations && <span className="text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded border border-green-500/20">Generated</span>}
          </div>
          <span className="text-[11px] text-[var(--text-muted)]">
            {hasConsiderations
              ? 'Review on the Considerations tab \u2014 ensures we request the right data and set correct expectations per country'
              : 'Go to Considerations tab to generate \u2014 critical for establishing expertise'}
          </span>
        </div>
        {hasConsiderations && countries.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {countries.map(code => (
              <span key={code} className="px-2 py-0.5 rounded text-[10px] bg-[var(--bg-tertiary)] text-[var(--text-secondary)] border border-[var(--border-color)]">
                {countryName(code)}
              </span>
            ))}
          </div>
        )}
      </Card>

      {/* Our FrankieOne Pass Rates */}
      {countries.length > 0 && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={14} className="text-cyan-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Our Pass Rates</h3>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            What pass rates is FrankieOne achieving in these countries? Pull from ThoughtSpot to demonstrate our capability.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {countries.map(code => {
              const cc = countryConfig[code] || {}
              const upd = (f: string, v: any) => {
                const all = { ...countryConfig, [code]: { ...cc, [f]: v } }
                saveDisc({ ...localDisc, country_config: all })
              }
              return (
                <div key={code} className="flex items-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2">
                  <span className="text-xs font-medium text-[var(--text-primary)] min-w-[80px]">{countryName(code)}</span>
                  <input type="number" value={cc.our_pass_rate || ''} onChange={e => upd('our_pass_rate', e.target.value)}
                    className="w-16 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)] text-center" placeholder="%" />
                  <span className="text-[10px] text-[var(--text-muted)]">%</span>
                </div>
              )
            })}
          </div>
          <button
            disabled={loadingPassRates}
            onClick={async () => {
              setLoadingPassRates(true)
              try {
                const res = await pocApi.loadPassRates(poc.id)
                const rates = (res?.data as any)?.rates || (res?.data as any)?.data?.rates
                if (rates && typeof rates === 'object') {
                  const updated = { ...countryConfig }
                  for (const [code, rate] of Object.entries(rates)) {
                    if (rate != null) {
                      updated[code] = { ...(updated[code] || {}), our_pass_rate: Math.round(rate as number * 10) / 10 }
                    }
                  }
                  saveDisc({ ...localDisc, country_config: updated })
                  showToast('Pass rates loaded from ThoughtSpot', 'success')
                } else {
                  showToast('No pass rate data returned', 'error')
                }
              } catch (err: any) {
                showToast(err?.message || 'Failed to load pass rates', 'error')
              }
              setLoadingPassRates(false)
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/25 rounded-lg hover:bg-cyan-500/20 transition-colors disabled:opacity-50"
          >
            {loadingPassRates ? <><Loader2 size={12} className="animate-spin" /> Loading from ThoughtSpot...</> : <><BarChart3 size={12} /> Load Pass Rates from ThoughtSpot</>}
          </button>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          PHASE 2: LAY FOUNDATIONS
          Get their rates, set expectations, build the business case
         ═══════════════════════════════════════════════════════════════════ */}
      {phaseHdr(2, <Hammer size={14} className="text-amber-400" />, 'Lay Foundations',
        'Get their current rates, set POC expectations, build the business case for switching', p2Done, p2Total)}

      {/* Customer Pass Rates & Targets */}
      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Customer Pass Rates & Targets</h3>
        <p className="text-xs text-[var(--text-muted)]">
          What are they getting today vs what they need? This defines success criteria.
        </p>
        {countries.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-2">Add countries in Phase 1 to configure per-country targets.</p>
        ) : (
          <div className="space-y-2">
            {countries.map(code => {
              const cc = countryConfig[code] || {}
              const cur = parseFloat(cc.current_rate) || 0, tgt = parseFloat(cc.target_rate) || 0
              const up = cur > 0 && tgt > 0 ? Math.round((tgt - cur) * 10) / 10 : null
              const name = countryName(code)
              const upd = (f: string, v: any) => {
                const all = { ...countryConfig, [code]: { ...cc, [f]: v } }
                saveDisc({ ...localDisc, country_config: all })
              }
              return (
                <div key={code} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-[var(--text-primary)]">{name}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                    {up != null && <span className={`text-[10px] font-semibold ${up > 0 ? 'text-green-400' : 'text-[var(--text-muted)]'}`}>{up > 0 ? `+${up}%` : `${up}%`} uplift</span>}
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    <div>
                      <label className={lc}>Current %</label>
                      <input type="number" value={cc.current_rate || ''} onChange={e => upd('current_rate', e.target.value)}
                        className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]" placeholder="55" />
                    </div>
                    <div>
                      <label className={lc}>Target %</label>
                      <input type="number" value={cc.target_rate || ''} onChange={e => upd('target_rate', e.target.value)}
                        className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]" placeholder="65" />
                    </div>
                    <div>
                      <label className={lc}>Current Vendor</label>
                      <input value={cc.current_vendor || ''} onChange={e => upd('current_vendor', e.target.value)}
                        className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]" placeholder="Sumsub" />
                    </div>
                    <div>
                      <label className={lc}>Consent</label>
                      <select value={cc.consent || 'unknown'} onChange={e => upd('consent', e.target.value)}
                        className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]">
                        {CONSENT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={lc}>Records</label>
                      <input type="number" value={cc.records || ''} onChange={e => upd('records', e.target.value)}
                        className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]" placeholder="500" />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Business Case */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <DollarSign size={14} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Business Case</h3>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          Why should they switch? Capture the cost of low pass rates &mdash; manual review time, staff costs, customer drop-off.
        </p>
        <div><label className={lc}>Problem to solve</label><DTextarea value={(localDisc.problem as string) || ''} onSave={v => saveDisc({ ...localDisc, problem: v })} rows={2} className={`mt-1 ${ic} !text-xs`} placeholder="e.g. Beat current 55% pass rate in LATAM, reduce manual review load" /></div>
        <div><label className={lc}>Business impact of low pass rates</label><DTextarea value={(localDisc.business_case_impact as string) || ''} onSave={v => saveDisc({ ...localDisc, business_case_impact: v })} rows={2} className={`mt-1 ${ic} !text-xs`} placeholder="e.g. 45% failure rate = ~2,000 manual reviews/month, 3 FTE at $80K each. Customer drop-off estimated at 15% of failures." /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={lc}>Manual review staff</label><DInput value={(localDisc.manual_review_staff as string) || ''} onSave={v => saveDisc({ ...localDisc, manual_review_staff: v })} className={`mt-1 ${ic}`} placeholder="e.g. 3 FTE" /></div>
          <div><label className={lc}>Monthly volume</label><DInput value={(localDisc.monthly_volume as string) || ''} onSave={v => saveDisc({ ...localDisc, monthly_volume: v })} className={`mt-1 ${ic}`} placeholder="e.g. 10,000" /></div>
          <div><label className={lc}>Cost per manual review</label><DInput value={(localDisc.cost_per_review as string) || ''} onSave={v => saveDisc({ ...localDisc, cost_per_review: v })} className={`mt-1 ${ic}`} placeholder="e.g. $5" /></div>
        </div>
      </Card>

      {/* POC Expectations & Commitment */}
      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">POC Expectations & Commitment</h3>
        <p className="text-xs text-[var(--text-muted)]">
          Set clear expectations: what the POC involves and what happens if we hit the target rates.
        </p>
        <div><label className={lc}>Success Criteria</label><DInput value={poc.success_criteria || ''} onSave={v => save({ success_criteria: v })} className={`mt-1 ${ic}`} placeholder="e.g. ~10% relative uplift needed to justify switching from current vendor" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lc}>Timeline</label><DInput value={(localDisc.poc_timeline as string) || ''} onSave={v => saveDisc({ ...localDisc, poc_timeline: v })} className={`mt-1 ${ic}`} placeholder="e.g. 2 weeks" /></div>
          <div><label className={lc}>POC cost</label><DInput value={(localDisc.poc_cost as string) || ''} onSave={v => saveDisc({ ...localDisc, poc_cost: v })} className={`mt-1 ${ic}`} placeholder="e.g. $2,500" /></div>
        </div>
        <div className="flex items-center gap-3 pt-2 border-t border-[var(--border-color)]">
          <button
            onClick={() => saveDisc({ ...localDisc, client_committed: !localDisc.client_committed })}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors cursor-pointer ${
              localDisc.client_committed
                ? 'bg-green-600/10 text-green-400 border-green-500/20 hover:bg-green-600/20'
                : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-primary)]'
            }`}
          >
            {localDisc.client_committed ? <CheckCircle2 size={12} /> : <Circle size={12} />}
            {localDisc.client_committed ? 'Client Committed' : 'Mark Client Committed'}
          </button>
          <span className="text-[10px] text-[var(--text-muted)]">Client agrees: if we hit target rates, they commit to moving to FrankieOne</span>
        </div>
      </Card>

      {/* POC Process Milestones */}
      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">POC Process Milestones</h3>
        <p className="text-xs text-[var(--text-muted)]">
          Track key handoff points in the POC process.
        </p>
        <div className="space-y-1.5">
          {([
            { key: 'config_sheet_sent', label: 'POC config Excel sheet sent to customer' },
            { key: 'config_confirmed', label: 'Customer confirmed POC configuration' },
            { key: 'agreement_sent', label: 'POC agreement sent to customer' },
            { key: 'agreement_signed', label: 'Customer signed POC agreement' },
          ] as const).map(({ key, label }) => (
            <button key={key}
              onClick={() => {
                const now = localDisc[key] ? null : new Date().toISOString()
                saveDisc({ ...localDisc, [key]: now })
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded-lg border transition-colors cursor-pointer text-left ${
                localDisc[key]
                  ? 'bg-green-600/10 text-green-400 border-green-500/20 hover:bg-green-600/20'
                  : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-primary)]'
              }`}
            >
              {localDisc[key] ? <CheckCircle2 size={12} /> : <Circle size={12} />}
              <span className="flex-1">{label}</span>
              {localDisc[key] && (
                <span className="text-[10px] text-[var(--text-muted)]">
                  {new Date(localDisc[key] as string).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Attached Documents */}
        <div className="pt-3 border-t border-[var(--border-color)] space-y-2">
          <div className="flex items-center gap-2">
            <Paperclip size={12} className="text-cyan-400" />
            <span className={lc}>Documents sent to customer</span>
          </div>
          {((localDisc.attached_documents || []) as { name: string; url: string }[]).map((doc, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-xs">
              <a href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 flex-1 min-w-0 text-cyan-400 hover:text-cyan-300 truncate">
                <ExternalLink size={10} className="shrink-0" />
                {doc.name}
              </a>
              <button onClick={() => {
                const docs = [...(localDisc.attached_documents || []) as { name: string; url: string }[]]
                docs.splice(i, 1)
                saveDisc({ ...localDisc, attached_documents: docs })
              }} className="p-0.5 text-[var(--text-muted)] hover:text-red-400 transition-colors">
                <Trash2 size={10} />
              </button>
            </div>
          ))}
          <div className="flex gap-2">
            <input id="doc-name" placeholder="Document name" className={`flex-1 ${ic} !text-xs`} />
            <input id="doc-url" placeholder="URL (Google Drive, Dropbox, etc.)" className={`flex-[2] ${ic} !text-xs`} />
            <button onClick={() => {
              const nameEl = document.getElementById('doc-name') as HTMLInputElement
              const urlEl = document.getElementById('doc-url') as HTMLInputElement
              if (!nameEl.value.trim() || !urlEl.value.trim()) return
              const docs = [...(localDisc.attached_documents || []) as { name: string; url: string }[], { name: nameEl.value.trim(), url: urlEl.value.trim() }]
              saveDisc({ ...localDisc, attached_documents: docs })
              nameEl.value = ''
              urlEl.value = ''
            }} className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-lg bg-cyan-600/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-600/20 transition-colors whitespace-nowrap">
              <Paperclip size={10} />
              Add
            </button>
          </div>
        </div>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════
          PHASE 3: CAPTURE & CONFIRM
          Get the data, confirm rulesets, enable secure transfer
         ═══════════════════════════════════════════════════════════════════ */}
      {phaseHdr(3, <FileCheck size={14} className="text-cyan-400" />, 'Capture & Confirm',
        'Configure match rulesets, send data template, get client confirmation, receive data', p3Done, p3Total)}

      {/* Match Configuration per country */}
      <Card className="p-4 space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Match Configuration</h3>
        <p className="text-xs text-[var(--text-muted)]">
          Configure vendors, matching criteria, and fuzziness per country. This determines how we run the POC.
        </p>
        {countries.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-2">Add countries in Phase 1 first.</p>
        ) : (
          <div className="space-y-4">
            {countries.map(code => {
              const cc = countryConfig[code] || {}
              const name = countryName(code)
              const upd = (f: string, v: any) => {
                const all = { ...countryConfig, [code]: { ...cc, [f]: v } }
                saveDisc({ ...localDisc, country_config: all })
              }
              const selectedVendors = (cc.vendors as string[]) || []

              return (
                <div key={code} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[var(--text-primary)]">{name}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">({code})</span>
                  </div>

                  {/* Vendors */}
                  <div>
                    <label className={lc}>Vendors</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {VENDORS.filter(v => !v.country_codes || v.country_codes.includes(code)).map(v => {
                        const on = selectedVendors.includes(v.id)
                        return <button key={v.id} type="button" onClick={() => upd('vendors', on ? selectedVendors.filter((s: string) => s !== v.id) : [...selectedVendors, v.id])}
                          className={`px-2 py-0.5 rounded text-[10px] font-medium border cursor-pointer ${on ? 'bg-cyan-500/15 text-cyan-400 border-cyan-500/40' : 'text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-secondary)]'}`}>
                          {on && '\u2713 '}{v.name.split('(')[0].trim()}
                        </button>
                      })}
                    </div>
                  </div>

                  {/* Match criteria */}
                  {(() => {
                    const s1: string[] = cc.match_source_1 || []
                    const s2: string[] = cc.match_source_2 || []
                    const { label: matchLabel, type: matchType } = deriveMatchLabel(s1, s2)
                    const addrSelected = s1.includes('address') || s2.includes('address')
                    const toggleEl = (source: 'match_source_1' | 'match_source_2', el: string) => {
                      const cur: string[] = cc[source] || []
                      upd(source, cur.includes(el) ? cur.filter((s: string) => s !== el) : [...cur, el])
                    }
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          {(['match_source_1', 'match_source_2'] as const).map((sourceKey, idx) => {
                            const selected: string[] = cc[sourceKey] || []
                            return (
                              <div key={sourceKey}>
                                <label className={lc}>Source {idx + 1}</label>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {MATCH_ELEMENTS.map(el => {
                                    const on = selected.includes(el.id)
                                    return <button key={el.id} type="button" onClick={() => toggleEl(sourceKey, el.id)}
                                      className={`px-2 py-0.5 rounded text-[10px] font-medium border cursor-pointer ${on ? 'bg-cyan-500/15 text-cyan-400 border-cyan-500/40' : 'text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-secondary)]'}`}>
                                      {on && '\u2713 '}{el.label}
                                    </button>
                                  })}
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        {matchLabel && (
                          <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-cyan-500/10 border border-cyan-500/30">
                            <span className="text-[10px] font-bold text-cyan-400">{matchType}</span>
                            <span className="text-[10px] text-cyan-400">{matchLabel}</span>
                          </div>
                        )}
                        {addrSelected && (
                          <div>
                            <label className={lc}>Address Matching</label>
                            <div className="flex items-center gap-2 mt-1">
                              <select value={cc.address_mode || ''} onChange={e => upd('address_mode', e.target.value)}
                                className="px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]">
                                <option value="">Select...</option>
                                <option value="partial">Partial</option>
                                <option value="full">Full</option>
                              </select>
                              {cc.address_mode && ADDRESS_MATCH_FIELDS.map((f: any) => {
                                const on = cc[`addr_${f.id}`] !== undefined ? cc[`addr_${f.id}`] : f.default
                                return <button key={f.id} type="button" onClick={() => upd(`addr_${f.id}`, !on)}
                                  className={`px-1.5 py-0.5 rounded text-[9px] border cursor-pointer ${on ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'text-[var(--text-muted)] border-[var(--border-color)]'}`}>
                                  {on ? '\u2713' : '\u25cb'} {f.label}
                                </button>
                              })}
                            </div>
                          </div>
                        )}
                        <div>
                          <label className={lc}>Fuzziness Notes</label>
                          <input value={cc.fuzziness_notes || ''} onChange={e => upd('fuzziness_notes', e.target.value)}
                            className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]"
                            placeholder="e.g. Exact name match required, DOB tolerates dd/mm vs mm/dd." />
                        </div>
                      </>
                    )
                  })()}

                  <div>
                    <label className={lc}>PASS/FAIL Description</label>
                    <input value={cc.pass_fail_description || ''} onChange={e => upd('pass_fail_description', e.target.value)}
                      className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]"
                      placeholder="e.g. Full name + DOB exact match = PASS." />
                  </div>
                  <div>
                    <label className={lc}>Special Requirements</label>
                    <textarea value={cc.special_notes || ''} onChange={e => upd('special_notes', e.target.value)} rows={2}
                      className="w-full mt-0.5 px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)] resize-none"
                      placeholder="e.g. Customer needs state returned in response — requires special Datazoo config" />
                  </div>

                  {/* Per-country client confirmation */}
                  <div className={`mt-2 pt-2 border-t border-[var(--border-color)] flex items-center justify-between`}>
                    <div className="flex items-center gap-2">
                      {cc.client_confirmed
                        ? <CheckCircle2 size={12} className="text-green-400" />
                        : <Circle size={12} className="text-[var(--text-muted)]" />}
                      <span className="text-[10px] text-[var(--text-secondary)]">
                        {cc.client_confirmed
                          ? `Client confirmed${cc.client_confirmed_at ? ` on ${new Date(cc.client_confirmed_at as string).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}`
                          : 'Awaiting client confirmation'}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        const nowConfirmed = !cc.client_confirmed
                        const updated = { ...countryConfig, [code]: { ...cc, client_confirmed: nowConfirmed, client_confirmed_at: nowConfirmed ? new Date().toISOString() : null } }
                        saveDisc({ ...localDisc, country_config: updated })
                        if (nowConfirmed && poc.slack_channel_id && poc.reminders_active) {
                          pocApi.createCanvas(poc.id).catch(() => {})
                        }
                      }}
                      className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded border transition-colors cursor-pointer ${
                        cc.client_confirmed
                          ? 'bg-green-600/10 text-green-400 border-green-500/20 hover:bg-green-600/20'
                          : 'bg-[var(--bg-primary)] text-[var(--text-muted)] border-[var(--border-color)] hover:text-[var(--text-primary)]'
                      }`}
                    >
                      {cc.client_confirmed ? <CheckCircle2 size={10} /> : <Circle size={10} />}
                      {cc.client_confirmed ? 'Confirmed' : 'Mark Confirmed'}
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Send all details to client for confirmation */}
        {countries.length > 0 && poc.slack_channel_id && poc.reminders_active && countries.some(c => !(countryConfig[c] || {}).client_confirmed) && (
          <div className="pt-3 border-t border-[var(--border-color)]">
            <button
              onClick={async () => {
                const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })
                const rulesetLines: string[] = []
                for (const code of countries) {
                  const cc2 = countryConfig[code] || {}
                  const s1: string[] = cc2.match_source_1 || []
                  const s2: string[] = cc2.match_source_2 || []
                  if (s1.length === 0 && s2.length === 0) continue
                  const { type: matchType } = deriveMatchLabel(s1, s2)
                  const allEls = [...new Set([...s1, ...s2])]
                  const elLabels = allEls.map((e: string) => MATCH_ELEMENTS.find(m => m.id === e)?.label || e).join(', ')
                  rulesetLines.push(`\u2022 ${countryName(code)}: ${matchType} with ${elLabels}`)
                }
                const dsLines: string[] = []
                for (const code of countries) {
                  const cc2 = countryConfig[code] || {}
                  const vendors = ((cc2.vendors || []) as string[]).filter(Boolean)
                  if (vendors.length > 0) {
                    const vendorNames = vendors.map((v: string) => VENDORS.find(vd => vd.id === v)?.name?.split('(')[0].trim() || v).join(', ')
                    dsLines.push(`\u2022 ${countryName(code)}: ${vendorNames}`)
                  }
                }
                const msgParts = [
                  `:warning: *These details have been sent to the client to confirm via Slack on ${today}.*`,
                  '',
                ]
                if (rulesetLines.length > 0) {
                  msgParts.push('*Ruleset*', ...rulesetLines, '')
                }
                const nameFuzzVals = countries.map(code => countryConfig[code]?.fuzziness_name as string || '').filter(Boolean)
                const addrFuzzVals = countries.map(code => countryConfig[code]?.fuzziness_address as string || '').filter(Boolean)
                const uniqueNameFuzz = [...new Set(nameFuzzVals)]
                const uniqueAddrFuzz = [...new Set(addrFuzzVals)]
                msgParts.push(
                  `*Name Fuzziness:* ${uniqueNameFuzz.length === 1 ? uniqueNameFuzz[0] : 'Have proposed standard'}`,
                  `*Address Fuzziness:* ${uniqueAddrFuzz.length === 1 ? uniqueAddrFuzz[0] : 'Have proposed standard'}`,
                )
                if (dsLines.length > 0) {
                  msgParts.push('', '*Datasources*', ...dsLines)
                }
                try {
                  const postRes = await pocApi.postStageUpdate(poc.id, msgParts.join('\n'))
                  if (postRes.success) {
                    showToast('Confirmation request posted to Slack', 'success')
                    refreshMessages()
                  } else {
                    showToast('Failed to post', 'error')
                  }
                } catch { showToast('Failed to post', 'error') }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600/10 text-amber-400 border border-amber-500/20 hover:bg-amber-600/20 transition-colors cursor-pointer"
            >
              <Send size={12} />
              Send Details to Client for Confirmation
            </button>
          </div>
        )}
      </Card>

      {/* Slack Channel */}
      <Card className="p-4 space-y-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Slack Channel</h3>

        <div>
          <label className="flex items-center gap-2 text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
            <Hash size={12} className="text-cyan-400" />
            Channel ID
          </label>
          <div className="flex items-center gap-2">
            {anonymised ? (
              <div className="flex-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-muted)] font-mono">
                {poc.slack_channel_id ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : 'C0123456789'}
              </div>
            ) : (
              <input type="text" value={poc.slack_channel_id || ''} onChange={e => save({ slack_channel_id: e.target.value || null })}
                placeholder="C0123456789"
                className="flex-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 focus:outline-none focus:border-cyan-500 transition-colors font-mono" />
            )}
            <span className="text-[10px] text-[var(--text-muted)] whitespace-nowrap">Find in channel settings</span>
          </div>
          <p className="text-[10px] text-amber-400/80 mt-1.5">
            Invite the bot first: type <code className="bg-[var(--bg-tertiary)] px-1 rounded">/invite @DealHub</code> in the channel.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={handleStartStop} disabled={remindersLoading}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
              poc.reminders_active ? 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
            } ${remindersLoading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {remindersLoading ? <Loader2 size={12} className="animate-spin" /> : poc.reminders_active ? <BellOff size={12} /> : <Bell size={12} />}
            {poc.reminders_active ? 'Disconnect Channel' : 'Connect Channel'}
          </button>
          {poc.reminders_active && !remindersLoading && (
            <>
              <button
                onClick={async () => {
                  setRefreshingCanvas(true)
                  try {
                    const canvasRes = await pocApi.createCanvas(poc.id)
                    if (canvasRes.success) {
                      const canvasId = (canvasRes.data as any)?.status_canvas_id
                      if (canvasId && !disc.canvas_id) save({ discovery_json: { ...localDisc, canvas_id: canvasId } as any })
                      showToast('Canvas refreshed', 'success')
                    } else {
                      showToast((canvasRes as any).error || 'Failed to refresh canvas', 'error')
                    }
                  } catch { showToast('Failed to refresh canvas', 'error') }
                  setRefreshingCanvas(false)
                }}
                disabled={refreshingCanvas}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border bg-blue-500/10 text-blue-400 border-blue-500/20 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
              >
                {refreshingCanvas ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Refresh Canvas
              </button>
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Connected</span>
            </>
          )}
          <div className="text-xs text-[var(--text-muted)]">
            {poc.reminders_active ? (
              <>
                <span>Active since {poc.reminders_started_at ? new Date(poc.reminders_started_at.replace(' ', 'T') + 'Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Melbourne' }) : 'now'}</span>
                {poc.slack_channel_id && !anonymised && <span className="ml-2">| <code className="text-[10px]">{poc.slack_channel_id}</code></span>}
              </>
            ) : <span>Not connected</span>}
          </div>
        </div>

        {connectProgress && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs text-cyan-400">
            <Loader2 size={12} className="animate-spin shrink-0" />
            <span>{connectProgress}</span>
          </div>
        )}

        {!poc.slack_channel_id && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-xs text-[var(--text-muted)]">
            <Info size={14} className="shrink-0 mt-0.5 text-cyan-400" />
            <div className="space-y-1">
              <p>Create a channel {!anonymised && <>(e.g. <code className="bg-[var(--bg-secondary)] px-1 rounded">#{suggestedChannelName}</code>)</>}, invite the bot, then paste the channel ID above.</p>
              <p>On connect, DealHub creates a canvas with POC details. Use <strong>Refresh Canvas</strong> to update it after changes.</p>
            </div>
          </div>
        )}
      </Card>

      {/* Quick Messages & Intro */}
      {poc.slack_channel_id && (
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare size={14} className="text-cyan-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Quick Messages</h3>
          </div>

          {poc.reminders_active && hasContext && (
            <div className="flex items-center gap-2 pb-2 border-b border-[var(--border-color)]">
              <button onClick={handleSendIntro} disabled={sendingIntro}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600/10 text-violet-400 border border-violet-500/20 hover:bg-violet-600/20 transition-colors disabled:opacity-50">
                {sendingIntro ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Post Intro Message
              </button>
              <span className="text-[10px] text-[var(--text-muted)]">Posts customer context to the channel</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            {PRE_POC_TEMPLATES.map(template => (
              <div key={template.id} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[var(--text-primary)]">{template.label}</div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{template.description}</div>
                </div>
                <button onClick={() => handleSendTemplate(template)} disabled={!!sendingTemplate || !poc.reminders_active}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-600/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-600/20 transition-colors disabled:opacity-50 whitespace-nowrap">
                  {sendingTemplate === template.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Post
                </button>
              </div>
            ))}
          </div>

          {!poc.reminders_active && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
              <Info size={14} className="shrink-0 mt-0.5" />
              <span>Connect the channel to enable posting.</span>
            </div>
          )}

          {poc.reminders_active && (
            <div className="space-y-2 pt-2 border-t border-[var(--border-color)]">
              <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)}
                placeholder="Type a custom update..."
                rows={2} className={`${ic} resize-none !text-xs`} />
              <div className="flex justify-end">
                <button onClick={handleSendCustom} disabled={sendingCustom || !customMessage.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 transition-colors disabled:opacity-50">
                  {sendingCustom ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Post Message
                </button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Message History */}
      {poc.slack_channel_id && (
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
          <button onClick={() => setMessagesOpen(!messagesOpen)}
            className="flex items-center justify-between w-full px-5 py-3.5 text-left hover:bg-[var(--bg-tertiary)] transition-colors">
            <div className="flex items-center gap-3">
              {messagesOpen ? <ChevronDown size={15} className="text-[var(--text-muted)]" /> : <ChevronRight size={15} className="text-[var(--text-muted)]" />}
              <MessageSquare size={15} className="text-cyan-400" />
              <span className="text-sm font-semibold text-[var(--text-primary)]">Message History</span>
              {slackMessages.length > 0 && (
                <span className="text-[10px] text-[var(--text-muted)]">{slackMessages.length} message{slackMessages.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </button>
          {messagesOpen && (
            <div className="border-t border-[var(--border-color)] p-4 space-y-3">
              {slackMessages.some(m => !m.slack_ts) && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <span>Some messages failed. Invite the bot (<code className="bg-[var(--bg-tertiary)] px-1 rounded">/invite @DealHub</code>), then Resend.</span>
                </div>
              )}
              {slackMessages.length > 0 ? (
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {slackMessages.map(msg => {
                    const typeColors: Record<string, string> = {
                      initial: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                      stage_update: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
                      reminder: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
                      nudge: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
                      complete: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                      canvas: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                      considerations: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
                    }
                    const failed = !msg.slack_ts
                    return (
                      <details key={msg.id} className={`rounded-lg border bg-[var(--bg-primary)] ${failed ? 'border-red-500/30' : 'border-[var(--border-color)]'}`}>
                        <summary className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors text-xs">
                          <span className="text-[10px] text-[var(--text-muted)] tabular-nums whitespace-nowrap">
                            {new Date(msg.created_at + 'Z').toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne' })} AEST
                          </span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${typeColors[msg.message_type] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
                            {msg.message_type.replace('_', ' ')}
                          </span>
                          {failed && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium border bg-red-500/10 text-red-400 border-red-500/20">failed</span>}
                          <span className="flex-1 text-[var(--text-muted)] truncate text-[11px]">{msg.message_text.slice(0, 80)}...</span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault(); e.stopPropagation()
                              if (resendingId === msg.id) return
                              setResendingId(msg.id)
                              try {
                                const res = await pocApi.resendSlackMessage(msg.id)
                                if (res.success && (res.data as any)?.resent) {
                                  showToast('Message resent', 'success')
                                  setSlackMessages(prev => prev.map(m => m.id === msg.id ? { ...m, slack_ts: (res.data as any).slack_ts } : m))
                                } else showToast('Resend failed', 'error')
                              } catch { showToast('Resend failed', 'error') }
                              setResendingId(null)
                            }}
                            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors whitespace-nowrap">
                            {resendingId === msg.id ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                            Resend
                          </button>
                        </summary>
                        <div className="px-3 pb-3 pt-1 border-t border-[var(--border-color)]">
                          <pre className="text-[11px] text-[var(--text-secondary)] whitespace-pre-wrap font-sans leading-relaxed">{msg.message_text}</pre>
                        </div>
                      </details>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-[var(--text-muted)] text-center py-2">
                  No messages yet.{!poc.reminders_active && ' Connect the channel to start posting.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


